<template>
  <v-container
    class="align-content-start"
    fill-height
    fluid
  >
    <v-row>
      <section-header-double-button-cancel
        :breadcrumb-items="breadcrumb"
        :primary-button-title="$t('Assumptions.EditAssumptions.Title')"
        :primary-button-icon="''"
        :primary-button="true"
        :primary-button-to="'EditAssumptions'"
        :query="{from: activeTab, intra: interestRateSection}"
        :is-draft-status="isDraftStatus"
        :secondary-button="activeTab === $t('RiskLiquidity.Tab.DeferenceReadiness.Slug')"
        :secondary-button-title="$t('RiskLiquidity.Section.DefenceReadiness.Button.Title')"
        :secondary-button-icon="''"
        :secondary-button-disabled="!editMode || !assumptionData"
        @click-secondary-button="handleButtonClick"
        @click-cancel="handleCancel"
      />
    </v-row>

    <!-- Tabs with Date Selector -->
    <v-row>
      <v-col cols="12">
        <div class="d-flex align-center justify-space-between">
          <v-tabs
            v-model="activeTab"
            active-class="active-tab"
            background-color="transparent"
            height="30"
            hide-slider
            show-arrows
          >
            <v-tab :href="`#${$t('RiskLiquidity.Tab.Monitoring.Slug')}`">
              {{ $t('RiskLiquidity.Tab.Monitoring.Title') }}
            </v-tab>
            <v-tab :href="`#${$t('RiskLiquidity.Tab.DeferenceReadiness.Slug')}`">
              {{ $t('RiskLiquidity.Tab.DeferenceReadiness.Title') }}
            </v-tab>
            <v-tab :href="`#${$t('RiskLiquidity.Tab.BankFailures.Slug')}`">
              {{ $t('RiskLiquidity.Tab.BankFailures.Title') }}
            </v-tab>
            <v-tab :href="`#${$t('RiskLiquidity.Tab.BankRunBenchmarking.Slug')}`">
              {{ $t('RiskLiquidity.Tab.BankRunBenchmarking.Title') }}
            </v-tab>
          </v-tabs>
        </div>
      </v-col>
    </v-row>

    <!-- Tabs Content -->
    <v-row ref="alm">
      <v-col cols="12">
        <v-tabs-items
          v-model="activeTab"
          class="fill-height"
        >
          <!-- Monitoring Tab -->
          <v-tab-item :value="$t('RiskLiquidity.Tab.Monitoring.Slug')">
            <risk-monitoring :breakpoint="breakpoint" />
          </v-tab-item>

          <!-- Defence Readiness Tab -->
          <v-tab-item :value="$t('RiskLiquidity.Tab.DeferenceReadiness.Slug')">
            <risk-defence-readiness :breakpoint="breakpoint" />
          </v-tab-item>

          <!-- Bank Failures Tab -->
          <v-tab-item :value="$t('RiskLiquidity.Tab.BankFailures.Slug')">
            <bank-failures :breakpoint="breakpoint" />
          </v-tab-item>

          <!-- Bank Run Benchmarking Tab -->
          <v-tab-item :value="$t('RiskLiquidity.Tab.BankRunBenchmarking.Slug')">
            <v-row>
              <v-col cols="12">
                <v-lazy
                  :options="{threshold: .5}"
                  transition="fade-transition"
                >
                  <bank-run-benchmarking :breakpoint="breakpoint" />
                </v-lazy>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs-items>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import i18nRouteMeta                     from '@/mixins/i18n/i18nRouteMeta'
import routerTabState                    from '@/mixins/routerTabState'
import SectionHeaderDoubleButtonCancel from '@/theme/default/components/common/SectionHeaderDoubleButtonCancel.vue'
import RiskMonitoring from '@/theme/default/views/banking/risk/liquidity/risk-monitoring/RiskMonitoring.vue'
import BankFailures from '@/theme/default/views/banking/risk/liquidity/bank-failures/BankFailures.vue'
import BankRunBenchmarking from '@/theme/default/views/banking/risk/liquidity/bank-run-benchmarking/BankRunBenchmarking.vue'
import RiskDefenceReadiness from '@/theme/default/views/banking/risk/liquidity/RiskDefenceReadiness.vue'

export default {
  name      : 'Liquidity',
  components: {
    SectionHeaderDoubleButtonCancel,
    RiskMonitoring,
    BankFailures,
    BankRunBenchmarking,
    RiskDefenceReadiness
  },
  mixins   : [i18nRouteMeta, routerTabState],
  dataStore: {
    appToolbarExtended          : 'App.Config.AppToolbarExtended',
    assumptionData              : 'RiskLiquidity.DepositBehavior.Assumptions',
    currentLiquidityParametersId: 'ActiveLiquidityParameters.currentLiquidityParametersId',
    isDraftLiquidity            : 'ActiveLiquidityParameters.isDraft'
  },
  data () {
    return {
      breakpoint         : (this.appToolbarExtended && this.$vuetify.breakpoint.smAndDown ? 12 : 6),
      minBlockHeight     : 364,
      editMode           : false,
      interestRateSection: 'RiskLiquidity'
    }
  },
  computed: {
    isDraftStatus () {
      return this.isDraftLiquidity
    },
    breadcrumb () {
      return [{
        text    : this.$t('RiskLiquidity.HeaderTitle'),
        disabled: true
      }]
    }
  },
  watch: {
    activeTab: {
      immediate: true,
      handler (newTab) {
        if (!newTab) {
          this.activeTab = this.$t('RiskLiquidity.Tab.Monitoring.Slug')
        }
      }
    },
    appToolbarExtended (val) {
      this.breakpoint = val && this.$vuetify.breakpoint.smAndDown ? 12 : 6
    },

    '$vuetify.breakpoint.smAndDown': {
      handler (val) {
        this.breakpoint = val && this.appToolbarExtended ? 12 : 6
      }
    }
  },
  created () {
    this.$bus.$on('RiskLiquidity:DefenceReadiness:EditModeChanged', (newVal) => {
      this.editMode = newVal
    })
  },
  beforeDestroy () {
    this.$bus.$off('RiskLiquidity:DefenceReadiness:EditModeChanged')
  },
  methods: {
    handleButtonClick () {
      this.$bus.$emit('RiskLiquidity:DefenceReadiness:Rerun', this.assumptionData)
    },
    handleCancel () {
      this.currentLiquidityParametersId = null
      this.isDraftLiquidity = false
      this.$bus.$emit('liquidity:cancel-assumptions-edit', {})
    },

    handleSave () {
      this.showSaveModal = true
    },

    closeSaveModal () {
      this.showSaveModal = false
    }
  }
}
</script>

<style scoped>
</style>
